"use client"

import { payment } from "@/actions/payment/client/payment"
import { CentraCheckoutScript } from "@/app/(site)/[locale]/checkout/centra/CentraCheckoutScript"
import { type AddressVerificationCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/addressVerificationCopy"
import { type ProductNoticesCopy } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/productNoticesCopy"
import { storedAddressForPaymentInit } from "@/app/(site)/[locale]/checkout/CheckoutContent/_utils/storedAddressForPaymentInit"
import OrderSummary from "@/app/(site)/[locale]/checkout/CheckoutContent/components/OrderSummery/OrderSummary"
import OutOfStockModal from "@/app/(site)/[locale]/checkout/CheckoutContent/modals/OutOfStockModal"
import Steps from "@/app/(site)/[locale]/checkout/CheckoutContent/Steps"
import {
    type MarketLayout,
    type ModalState,
    type OutOfStockItem
} from "@/app/(site)/[locale]/checkout/types"
import ContentMapperCheckout from "@/app/(site)/_components/ContentMapperCheckout"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { usePaymentResultEvent } from "@/app/(site)/_utils/_hooks/usePaymentResultEvent"
import logger from "@/lib/logger/logger-init"
import {
    baseUrl,
    FIRST_INIT_CHECKOUT_STORAGE_KEY,
    PAYMENT_FAILED_URL,
    successUrl,
    UNAVAILABLE_ITEMS_TYPE
} from "@/lib/siteconfig/constants"
import { useAppForCurrentLocale } from "@/src/utils/locale/useAppForCurrentLocale"
import { t } from "@/translations"

import { type CheckoutPageSettingsContent } from "@/types"
import { useEffect, useMemo, useRef, useState } from "react"

interface CheckoutFlowProps {
    params: { locale: string }
    langCode: string
    marketLayout: MarketLayout
    emptyCart: CheckoutPageSettingsContent["emptyCart"] | null
    notificationBar: CheckoutPageSettingsContent["notificationBar"] | null
    customerService: CheckoutPageSettingsContent["customerService"] | null
    paymentPrivacyNotice: CheckoutPageSettingsContent["paymentPrivacyNotice"] | null
    addressVerificationCopy: AddressVerificationCopy
    productNoticesCopy: ProductNoticesCopy
    /**
     * Server-resolved active-basket gate (read from the `basket_session` cookie at request
     * time). Used as the first-paint fallback while the client SWR selection is still loading,
     * so the checkout form doesn't flash in before flipping to empty for visitors with no
     * active basket (e.g. returning to /checkout after an order).
     */
    initialHasActiveBasket: boolean
}

export default function CheckoutFlow({
    params,
    langCode,
    marketLayout,
    emptyCart,
    notificationBar,
    customerService,
    paymentPrivacyNotice,
    addressVerificationCopy,
    productNoticesCopy,
    initialHasActiveBasket
}: CheckoutFlowProps) {
    const { selection, mutate, isLoadingSelection, hasActiveBasket } =
        useSWRBasketSessionState()
    const [formHtml, setFormHtml] = useState<string | null>(null)
    const [isLoadingFormHtml, setIsLoadingFormHtml] = useState(false)
    const [modalState, setModalState] = useState<ModalState>({ type: "closed" })
    const hasInitializedPayment = useRef(false)
    const { path: localePath } = useAppForCurrentLocale()
    const isRoutingToPaymentResult = usePaymentResultEvent()
    // Initialize Adyen Drop-in formHtml according to Centra documentation.
    //
    // The address sent here is the one Centra already holds for this selection, reduced
    // to its non-empty fields. With `paymentInitiateOnly: true` Centra treats a missing
    // address as the new address and blanks what it had stored, email included, which
    // parks every email-conditioned voucher until the email is written back. Sending
    // Centra its own current values keeps that intact and cannot re-anchor the country,
    // because the country sent is the one the selection already has. Form input still
    // reaches Centra only through the dedicated `paymentFields` flow.
    const initializeAdyenFormHtml = async () => {
        if (!selection) return
        setIsLoadingFormHtml(true)
        try {
            const errorUrl = `${baseUrl}${localePath}/${PAYMENT_FAILED_URL}`

            const params = {
                paymentMethod: selection.paymentMethod,
                paymentInitiateOnly: true,
                language: langCode,
                paymentReturnPage: successUrl,
                paymentFailedPage: errorUrl,
                address: storedAddressForPaymentInit(selection.address),
                shippingAddress: storedAddressForPaymentInit(selection.shippingAddress)
            }

            const response = await payment(params)
            if (response._tag === "failure") {
                const errorDetails = (response.error as Error).cause as any
                if (errorDetails?.errors?.stock === UNAVAILABLE_ITEMS_TYPE) {
                    const totalRemainingItemsInBasket =
                        errorDetails.selection.items.reduce(
                            (acc: number, item: any) => acc + item.quantity,
                            0
                        )
                    setModalState({
                        type: "some_items_unavailable",
                        items: errorDetails.unavailable,
                        totalRemainingItemsInBasket: totalRemainingItemsInBasket || 0
                    })
                    await mutate()
                }
                logger.error(
                    "Failed to initialize Adyen Drop-in formHtml",
                    response.error
                )
                await mutate()
                return
            }

            const html = response.data?.paymentResult?.formHtml
            if (html) {
                setFormHtml(html)
                await mutate()
            }
        } catch (error) {
            logger.error("Error initializing Adyen Drop-in formHtml", error)
        } finally {
            setIsLoadingFormHtml(false)
        }
        // TODO: maybe unmount the formHtml
    }

    useEffect(() => {
        if (
            selection &&
            (selection.items?.length ?? 0) > 0 &&
            !isLoadingFormHtml &&
            !hasInitializedPayment.current
        ) {
            hasInitializedPayment.current = true
            if (typeof window !== "undefined") {
                sessionStorage.setItem(FIRST_INIT_CHECKOUT_STORAGE_KEY, "true")
            }
            initializeAdyenFormHtml()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selection)])

    // Keep for now!
    // @todo: Below to use /payment-fields to retrieve centra_checkout_script when POC starts using Klarna checkout or Ingrid
    // const centraCheckoutCallback = async (origdata: Record<string, any>) => {
    //     if (origdata.detail) {
    //         const data = { ...origdata.detail }

    //         // Handle Adyen Drop-in payment callback
    //         if (data.paymentMethodSpecificFields && selection) {
    //             try {
    //                 // Build params object - include paymentMethodSpecificFields from Adyen
    //                 const params: any = {
    //                     paymentMethod: selection.paymentMethod,
    //                     termsAndConditions: true,
    //                     language: langCode
    //                 }

    //                 // Add paymentMethodSpecificFields from Adyen Drop-in event
    //                 if (data.paymentMethodSpecificFields) {
    //                     params.paymentMethodSpecificFields =
    //                         data.paymentMethodSpecificFields
    //                 }

    //                 // Add address information if available
    //                 if (data.billingAddress) {
    //                     params.address = data.billingAddress
    //                 }
    //                 if (data.shippingAddress) {
    //                     params.shippingAddress = data.shippingAddress
    //                 }

    //                 // Call Centra payment API with the encrypted payment data
    //                 const centraResponse = await refreshCheckout(initiatePayment(params))

    //                 if (centraResponse._tag === "success") {
    //                     logger.info(
    //                         "Payment fields updated successfully via Centra callback"
    //                     )

    //                     // Update the selection with new data from the callback
    //                     await mutate()
    //                     const formHtml = centraResponse.data?.paymentResult?.formHtml

    //                     if (formHtml) {
    //                         setFormHtml(formHtml)
    //                     }
    //                 }
    //             } catch (error) {
    //                 logger.error("Error in centraCheckoutCallback", {
    //                     error,
    //                     message: error instanceof Error ? error.message : String(error)
    //                 })
    //             }
    //         }
    //     }
    // }

    const isCartEmpty = useMemo(() => {
        // If routing to payment result, treat cart as empty to show empty cart UI
        if (isRoutingToPaymentResult) {
            return false
        }
        if (isLoadingSelection) {
            // Before the live selection arrives, defer to the server-resolved cookie gate.
            // A completed-order / preference cookie has no active basket → show empty
            // immediately (no checkout-form flash). An active cookie → keep the form so a
            // real basket never flashes the empty state while loading.
            return !initialHasActiveBasket
        }
        // No active basket (e.g. the token was retired after an order, or the session
        // expired) means there is nothing to check out. Show the empty-cart UI instead of
        // the form — rendering the form here would let Centra-bound actions (country
        // change, payment) fire without a token and throw "No valid session token found".
        if (!hasActiveBasket) {
            return true
        }
        return selection?.items?.length === 0
    }, [
        isRoutingToPaymentResult,
        hasActiveBasket,
        initialHasActiveBasket,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(selection),
        selection?.items?.length,
        isLoadingSelection
    ])

    return isCartEmpty ? (
        <>
            <main className="mb-64 mt-56 pt-0 sm:pt-32 md:mb-144 md:pt-40">
                <div className="flex flex-col px-12 py-24 sm:px-16 sm:py-24 md:p-24">
                    {emptyCart?.content && (
                        <ContentMapperCheckout
                            content={emptyCart?.content}
                            params={params}
                            isFirstBlockAboveTheFold={true}
                        />
                    )}
                </div>
            </main>
            <OutOfStockModal
                isOpen={modalState.type !== "closed"}
                type="cart_empty"
                langCode={langCode}
                onClose={() => {
                    setModalState({ type: "closed" })
                }}
                items={
                    modalState.type === "some_items_unavailable"
                        ? (modalState.items as OutOfStockItem[])
                        : []
                }
                totalRemainingItemsInBasket={
                    modalState.type === "some_items_unavailable"
                        ? modalState.totalRemainingItemsInBasket
                        : 0
                }
                isCartEmpty={isCartEmpty}
            />
        </>
    ) : (
        <main className="px-0 sm:px-46 md:px-24">
            <h1 className="sr-only">{t(langCode, "checkout.checkout_header")}</h1>
            <OutOfStockModal
                isOpen={modalState.type !== "closed"}
                type="some_items_unavailable"
                langCode={langCode}
                onClose={() => {
                    setModalState({ type: "closed" })
                }}
                items={
                    modalState.type === "some_items_unavailable"
                        ? (modalState.items as OutOfStockItem[])
                        : []
                }
                totalRemainingItemsInBasket={
                    modalState.type === "some_items_unavailable"
                        ? modalState.totalRemainingItemsInBasket
                        : 0
                }
                isCartEmpty={isCartEmpty}
            />
            <CentraCheckoutScript formHtml={formHtml} />
            <div className="flex h-[calc(100vh-48px)] flex-col bg-col-bg-primary text-col-text-primary md:flex-row md:justify-center">
                <div className="order-2 w-full md:order-1 md:w-1/2 md:max-w-[648px]">
                    <Steps
                        langCode={langCode}
                        params={params}
                        marketLayout={marketLayout}
                        notificationBar={notificationBar}
                        paymentPrivacyNotice={paymentPrivacyNotice}
                        addressVerificationCopy={addressVerificationCopy}
                        productNoticesCopy={productNoticesCopy}
                    />
                </div>
                <div className="order-1 w-full bg-col-bg-soft md:order-2 md:w-1/2 md:max-w-[648px] md:overflow-y-auto">
                    <OrderSummary
                        showVoucherInput={true}
                        showShipping={true}
                        langCode={langCode}
                        className="h-full"
                        customerService={customerService}
                        marketLayout={marketLayout}
                        productNoticesCopy={productNoticesCopy}
                    />
                </div>
            </div>
        </main>
    )
}
